import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ScrollTableWrapper } from "../../pages/SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { TextButton } from "../Buttons/Buttons";
import { SoftHeader2 } from "../Typography/Typography";
import { Table } from "./Table";
import type { ITableProps, TableRefProps } from "./utils";

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TableContainer = styled.div`
  & div[class*="TableWrapper"] {
    overflow: visible;
    td {
      overflow: visible;
    }
  }
`;

const BulkActionWrapper = styled.div`
  display: flex;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-top: 1px solid ${({ theme }) => theme.secondaryBorder};
  border-right: 1px solid ${({ theme }) => theme.secondaryBorder};
  border-left: 4px solid ${({ theme }) => theme.secondaryBorder};
`;

const ResetBtnWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const TableWithBulkAction = forwardRef<
  TableRefProps,
  ITableProps<any> & {
    bulkActionElement: <
      ElementProps extends {
        rows: string[] | "all";
        resetSelectedRows: () => void;
      }
    >(
      props: ElementProps
    ) => JSX.Element;
  }
>(
  <TableData extends object>(
    props: ITableProps<TableData> & {
      bulkActionElement: <
        ElementProps extends {
          rows: string[] | "all";
          resetSelectedRows: () => void;
        }
      >(
        props: ElementProps
      ) => JSX.Element;
    },
    ref: React.ForwardedRef<TableRefProps>
  ) => {
    const {
      data,
      handleSelectRows,
      bulkActionElement,
      bulkSelectLimit,
      maxHeight,
      scrollRef,
      onScroll,
      defaultSelectedRows,
      portalId = "dropdown-root",
    } = props;

    const rowsSelected = useMemo(
      () => defaultSelectedRows ?? [],
      [defaultSelectedRows]
    );
    const resetSelectedRowsRef = useRef<TableRefProps>(null);
    const previousSelectedRows = useRef<string[] | "all">(rowsSelected);

    // Reset row selection externally
    useEffect(() => {
      const wasSelected = Array.isArray(previousSelectedRows.current)
        ? previousSelectedRows.current.length > 0
        : previousSelectedRows.current === "all";
      const isNowEmpty =
        Array.isArray(rowsSelected) && rowsSelected.length === 0;

      // If we had selections before and now we don't, reset the table checkboxes
      if (wasSelected && isNowEmpty && resetSelectedRowsRef.current) {
        resetSelectedRowsRef.current.resetIsDefaultSelected();
      }

      previousSelectedRows.current = rowsSelected;
    }, [rowsSelected]);

    const { t } = useTranslation();

    const disableTableSelect = data.some(
      (el) => (el as { canSelect: boolean }).canSelect === false
    );

    const onSelectRows = (rows: string[] | "all") => {
      handleSelectRows?.(rows);
    };

    const handleResetRows = useCallback(() => {
      resetSelectedRowsRef?.current?.resetSelectedRows();
      handleSelectRows?.([]);
    }, [handleSelectRows]);

    useImperativeHandle(
      ref,
      () => ({
        resetSelectedRows: handleResetRows,
        selectAllItems: () => resetSelectedRowsRef?.current?.selectAllItems(),
        resetIsDefaultSelected: () =>
          resetSelectedRowsRef?.current?.resetIsDefaultSelected(),
      }),
      [handleResetRows]
    );

    return (
      <TableContainer className="table-with-bulk-action">
        {rowsSelected.length > 0 && !!resetSelectedRowsRef.current ? (
          <BulkActionWrapper>
            <ResetBtnWrapper>
              <SoftHeader2>
                {rowsSelected === "all"
                  ? t("All items selected")
                  : `${rowsSelected.length} ${t("selected")}`}
              </SoftHeader2>
              <TextButton onClick={handleResetRows}>
                <SoftHeader2>{t("Unselect All")}</SoftHeader2>
              </TextButton>
              {rowsSelected !== "all" &&
              !disableTableSelect &&
              !bulkSelectLimit ? (
                <TextButton
                  onClick={resetSelectedRowsRef.current.selectAllItems}
                >
                  <SoftHeader2>{t("Select All")}</SoftHeader2>
                </TextButton>
              ) : (
                <></>
              )}
            </ResetBtnWrapper>
            {bulkActionElement({
              rows: rowsSelected,
              resetSelectedRows: handleResetRows,
            })}
          </BulkActionWrapper>
        ) : (
          <></>
        )}
        <ScrollTableWrapper
          maxHeight={
            rowsSelected.length > 0 && maxHeight === "100%"
              ? `calc(100% - 63px)`
              : maxHeight
          }
          ref={scrollRef as React.RefObject<HTMLDivElement>}
          onScroll={onScroll}
        >
          <TableWrapper>
            <Table
              {...props}
              handleSelectRows={onSelectRows}
              ref={resetSelectedRowsRef}
              disableTableSelection={disableTableSelect}
            />
            <div id={portalId}></div>
          </TableWrapper>
        </ScrollTableWrapper>
      </TableContainer>
    );
  }
);
